/**
 * useModalA11y — minimal accessibility upgrade for existing modal markup.
 *
 * Wire the returned ref onto your modal's outer element, pass `open`, and get:
 *   • Focus trap (tab cycles within the modal only)
 *   • Escape key closes the modal (unless disabled via `disableEscape`)
 *   • On open, moves focus to the first focusable element
 *   • On close, restores focus to whichever element was focused before open
 *
 * Deliberately does NOT alter DOM structure — pair with `role="dialog"`,
 * `aria-modal="true"` and `aria-labelledby` on the same element.
 *
 * Usage:
 *   const dialogRef = useModalA11y({ open: showModal, onClose: () => setShow(false) });
 *   return <div ref={dialogRef} role="dialog" aria-modal="true" ...>...</div>;
 */
import { useEffect, useRef } from "react";

const FOCUSABLE_SELECTOR = [
  "button:not([disabled])",
  "[href]",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

export default function useModalA11y({ open, onClose, disableEscape = false, disableAutoFocus = false }) {
  const ref = useRef(null);
  const previouslyFocusedRef = useRef(null);
  // Latest onClose is stashed in a ref so the effect below only re-runs when
  // `open` actually changes — otherwise every parent render would tear down
  // the focus trap and re-focus the "previously focused" element, stealing
  // focus away from inputs in modals stacked on top (e.g. the delete-account
  // dialog opened while the onboarding tour is still mounted).
  const onCloseRef = useRef(onClose);
  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!open) return;
    previouslyFocusedRef.current = typeof document !== "undefined" ? document.activeElement : null;

    if (!disableAutoFocus && ref.current) {
      // Defer to next frame so the modal is painted and focusable elements
      // exist in the DOM.
      requestAnimationFrame(() => {
        const focusables = ref.current?.querySelectorAll(FOCUSABLE_SELECTOR);
        const first = focusables && focusables[0];
        if (first && typeof first.focus === "function") first.focus();
      });
    }

    const onKey = (e) => {
      if (e.key === "Escape" && !disableEscape) {
        e.stopPropagation();
        onCloseRef.current && onCloseRef.current();
        return;
      }
      if (e.key !== "Tab" || !ref.current) return;
      const focusables = Array.from(ref.current.querySelectorAll(FOCUSABLE_SELECTOR))
        .filter((el) => el.offsetParent !== null || el === document.activeElement);
      if (focusables.length === 0) {
        e.preventDefault();
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      if (e.shiftKey && active === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && active === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      // Restore focus to the previously-focused element (unless it's since been removed).
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === "function" && document.contains(prev)) {
        prev.focus();
      }
    };
  }, [open, disableEscape, disableAutoFocus]);

  return ref;
}
